import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  FaShareAlt,
  FaCopy,
  FaGift,
  FaUsers,
  FaCoins,
  FaWhatsapp,
  FaTelegram,
  FaEnvelope,
  FaCheckCircle,
  FaSpinner,
  FaLink,
  FaStar,
  FaUserPlus,
  FaTimes
} from "react-icons/fa";

const API_BASE_URL = 'https://api.ingrainsystems.com/api';

const MyReferral = () => {
  const [referralCode, setReferralCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const getClientId = () => {
    return localStorage.getItem('clientId') || '';
  };

  useEffect(() => {
    fetchReferralCode();
  }, []);

  const fetchReferralCode = async () => {
    try {
      setLoading(true);
      const clientId = getClientId();
      
      if (!clientId) {
        setError("Client ID not found. Please login again.");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/clients/get-referral-code/${clientId}`);
      
      console.log("Referral API Response:", response.data);
      
      if (response.data.success) {
        setReferralCode(response.data.referralCode);
      } else {
        setError(response.data.message || "Failed to fetch referral code");
      }
    } catch (err) {
      console.error("Error fetching referral code:", err);
      setError(err.response?.data?.message || "Failed to fetch referral code");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      showToast("Referral link copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      showToast("Failed to copy link", "error");
    }
  };

  const copyCodeOnly = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      showToast("Referral code copied!", "success");
    } catch (err) {
      showToast("Failed to copy code", "error");
    }
  };

  const shareOnWhatsApp = () => {
    const text = `🎉 Join me on Timely Health Care! Use my referral code: ${referralCode} to get started. Sign up here: ${window.location.origin}/signup?ref=${referralCode}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareOnTelegram = () => {
    const text = `🎉 Join me on Timely Health Care! Use my referral code: ${referralCode} to get started. Sign up here: ${window.location.origin}/signup?ref=${referralCode}`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(window.location.origin + '/signup?ref=' + referralCode)}&text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareByEmail = () => {
    const subject = "Join me on Timely Health Care";
    const body = `Hi,\n\nI'm using Timely Health Care and I think you'll love it too!\n\nUse my referral code: ${referralCode} when you sign up.\n\nSign up here: ${window.location.origin}/signup?ref=${referralCode}\n\nJoin now and get benefits!`;
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank');
  };

  const shareNative = async () => {
    const shareData = {
      title: 'Timely Health Care Referral',
      text: `Join me on Timely Health Care! Use my referral code: ${referralCode}`,
      url: `${window.location.origin}/signup?ref=${referralCode}`
    };
    
    try {
      if (navigator.share) {
        setShareLoading(true);
        await navigator.share(shareData);
        showToast("Shared successfully!", "success");
      } else {
        copyToClipboard();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        showToast("Share failed", "error");
      }
    } finally {
      setShareLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Loading referral code...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-lg">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <FaUsers className="text-red-500 text-xl" />
          </div>
          <h3 className="text-base font-bold text-gray-800 mb-1">Referral Error</h3>
          <p className="text-xs text-gray-500 mb-4">{error}</p>
          <button
            onClick={fetchReferralCode}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;

  return (
    <div className="p-4 md:p-6">
      {/* Custom Toast Notification */}
      {toast.show && (
        <div className="fixed top-5 right-5 z-50 animate-in slide-in-from-top-2 duration-300">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg ${
            toast.type === "success" ? "bg-emerald-500" : "bg-red-500"
          } text-white text-xs font-medium`}>
            {toast.type === "success" ? <FaCheckCircle /> : <FaTimes />}
            {toast.message}
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
            <FaUsers className="text-white text-2xl" />
          </div>
          <h1 className="text-xl font-bold text-gray-800">Refer & Earn</h1>
          <p className="text-xs text-gray-500 mt-1">Invite friends and earn rewards</p>
        </div>

        {/* Referral Code Card */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-5 mb-5 shadow-lg">
          <div className="text-center text-white mb-3">
            <p className="text-[10px] font-bold opacity-90 uppercase tracking-wider">Your Referral Code</p>
            <div className="flex items-center justify-center gap-2 my-2">
              <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-lg">
                <span className="text-2xl font-black tracking-wider">{referralCode}</span>
              </div>
              <button
                onClick={copyCodeOnly}
                className="bg-white/20 backdrop-blur p-2 rounded-lg hover:bg-white/30 transition"
              >
                <FaCopy className="text-white text-sm" />
              </button>
            </div>
            <p className="text-[10px] opacity-90">Share this code with your friends</p>
          </div>
        </div>

       

        {/* Share Options */}
        <div className="bg-white rounded-xl p-4 mb-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <FaShareAlt className="text-emerald-500 text-sm" />
            <h3 className="text-sm font-bold text-gray-800">Share via</h3>
          </div>
          
          <div className="grid grid-cols-4 gap-2">
            <button
              onClick={shareOnWhatsApp}
              className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <FaWhatsapp className="text-white text-lg" />
              </div>
              <span className="text-[10px] text-gray-600">WhatsApp</span>
            </button>
            
            <button
              onClick={shareOnTelegram}
              className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="w-10 h-10 bg-blue-400 rounded-full flex items-center justify-center">
                <FaTelegram className="text-white text-lg" />
              </div>
              <span className="text-[10px] text-gray-600">Telegram</span>
            </button>
            
            <button
              onClick={shareByEmail}
              className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center">
                <FaEnvelope className="text-white text-lg" />
              </div>
              <span className="text-[10px] text-gray-600">Email</span>
            </button>
            
            <button
              onClick={shareNative}
              disabled={shareLoading}
              className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                {shareLoading ? (
                  <FaSpinner className="text-white text-lg animate-spin" />
                ) : (
                  <FaShareAlt className="text-white text-lg" />
                )}
              </div>
              <span className="text-[10px] text-gray-600">More</span>
            </button>
          </div>
        </div>

        {/* How it Works */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <FaGift className="text-emerald-500 text-sm" />
            <h3 className="text-sm font-bold text-gray-800">How Refer & Earn Works</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-emerald-600">1</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">Share Your Code</p>
                <p className="text-[10px] text-gray-500">Share your unique referral code with friends</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-emerald-600">2</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">Friend Signs Up</p>
                <p className="text-[10px] text-gray-500">Your friend uses your code while signing up</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-emerald-600">3</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">Earn Rewards</p>
                <p className="text-[10px] text-gray-500">Get bonus coins when they purchase a plan</p>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
            <div className="flex items-center gap-2">
              <FaStar className="text-amber-500 text-xs" />
              <p className="text-[10px] font-medium text-amber-700">
                Each successful referral earns you <strong>100 coins</strong>!
              </p>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="mt-4 bg-blue-50 rounded-lg p-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <FaCoins className="text-white text-sm" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold text-blue-800">Referral Benefits</p>
              <p className="text-[8px] text-blue-600">1 Referral = 100 Coins • Unlimited referrals • Lifetime tracking</p>
            </div>
            <div className="text-right">
              <FaUserPlus className="text-blue-400 text-sm" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MyReferral;